// Zentraler fetch-Wrapper.
//
// Aufgaben:
//  - JWT aus localStorage automatisch in Authorization-Header schreiben
//  - bei 401 Token verwerfen und auf /login redirecten
//  - JSON-Body automatisch (de)serialisieren
//  - einheitliche Fehler werfen (Error mit .status und .body)

const TOKEN_KEY = 'lms_admin_token';
const USER_KEY = 'lms_admin_user';
const ROLE_KEY = 'lms_admin_role';

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}

export function getUser() {
    return localStorage.getItem(USER_KEY);
}

export function getRole() {
    return localStorage.getItem(ROLE_KEY);
}

export function isAdmin() {
    return getRole() === 'admin';
}

export function setAuth(token, username, role) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, username);
    if (role) localStorage.setItem(ROLE_KEY, role);
}

export function clearAuth() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_KEY);
}

/**
 * Interner fetch-Helper. Akzeptiert sowohl JSON- als auch leere/CSV-
 * Responses und wirft bei !ok einen Error mit Statuscode.
 *
 * Bei 401 wird der Token verworfen und ein Browser-Redirect auf /login
 * ausgelöst – das vereinfacht den Routenschutz erheblich.
 */
async function request(method, path, { body, query, raw } = {}) {
    const headers = {};
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let url = path;
    if (query && Object.keys(query).length > 0) {
        const usp = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== null && v !== '') usp.set(k, v);
        }
        const qs = usp.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }

    let bodyToSend;
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        bodyToSend = JSON.stringify(body);
    }

    const res = await fetch(url, { method, headers, body: bodyToSend });

    if (res.status === 401) {
        clearAuth();
        // Nur redirecten, wenn wir nicht ohnehin schon auf /login sind.
        if (!window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
        }
        const err = new Error('Unauthorized');
        err.status = 401;
        throw err;
    }

    if (raw) return res;

    if (res.status === 204) return null;

    const text = await res.text();
    const data = text ? safeParse(text) : null;

    if (!res.ok) {
        const err = new Error((data && data.error) || `HTTP ${res.status}`);
        err.status = res.status;
        err.body = data;
        throw err;
    }
    return data;
}

function safeParse(text) {
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

// --- High-Level-API -----------------------------------------------------

export const api = {
    // Auth
    login: (username, password) =>
        request('POST', '/api/auth/login', { body: { username, password } }),
    me: () => request('GET', '/api/auth/me'),
    changeOwnPassword: (current_password, new_password) =>
        request('POST', '/api/auth/change-password', {
            body: { current_password, new_password },
        }),

    // User-Management (Admin)
    listUsers: () => request('GET', '/api/users'),
    createUser: (data) => request('POST', '/api/users', { body: data }),
    updateUser: (id, data) => request('PATCH', `/api/users/${id}`, { body: data }),
    setUserPassword: (id, new_password) =>
        request('PATCH', `/api/users/${id}/password`, { body: { new_password } }),
    deleteUser: (id) => request('DELETE', `/api/users/${id}`),

    // Mandanten
    listMandanten: (params) => request('GET', '/api/mandanten', { query: params }),
    getMandant: (id) => request('GET', `/api/mandanten/${id}`),
    createMandant: (data) => request('POST', '/api/mandanten', { body: data }),
    updateMandant: (id, data) => request('PUT', `/api/mandanten/${id}`, { body: data }),
    deleteMandant: (id) => request('DELETE', `/api/mandanten/${id}`),

    // Events
    listEvents: (params) => request('GET', '/api/events', { query: params }),
    getEvent: (id) => request('GET', `/api/events/${id}`),
    createEvent: (data) => request('POST', '/api/events', { body: data }),
    updateEvent: (id, data) => request('PUT', `/api/events/${id}`, { body: data }),
    updateEventStatus: (id, status) =>
        request('PATCH', `/api/events/${id}/status`, { body: { status } }),
    deleteEvent: (id) => request('DELETE', `/api/events/${id}`),

    // User-Options (Autor-Dropdown – nur id+username, ohne Admin-Rechte)
    listUserOptions: () => request('GET', '/api/auth/users'),

    // Totara-Einstellungen (dokumentiert, global oder je Mandant)
    listTotaraSettings: (params) => request('GET', '/api/totara/settings', { query: params }),
    getTotaraSetting: (id) => request('GET', `/api/totara/settings/${id}`),
    createTotaraSetting: (data) => request('POST', '/api/totara/settings', { body: data }),
    updateTotaraSetting: (id, data) =>
        request('PUT', `/api/totara/settings/${id}`, { body: data }),
    deleteTotaraSetting: (id) => request('DELETE', `/api/totara/settings/${id}`),
    listTotaraCategories: () => request('GET', '/api/totara/categories'),
    listTotaraCourseSets: () => request('GET', '/api/totara/course-sets'),

    // Kurseinstellungen (Ereignisse zum Kursangebot)
    listCourseSettingEvents: (params) =>
        request('GET', '/api/course-settings', { query: params }),
    getCourseSettingEvent: (id) => request('GET', `/api/course-settings/${id}`),
    createCourseSettingEvent: (data) =>
        request('POST', '/api/course-settings', { body: data }),
    updateCourseSettingEvent: (id, data) =>
        request('PUT', `/api/course-settings/${id}`, { body: data }),
    updateCourseSettingEventStatus: (id, status) =>
        request('PATCH', `/api/course-settings/${id}/status`, { body: { status } }),
    deleteCourseSettingEvent: (id) => request('DELETE', `/api/course-settings/${id}`),

    // Rechnungen
    listInvoices: (params) => request('GET', '/api/invoices', { query: params }),
    createInvoice: (data) => request('POST', '/api/invoices', { body: data }),
    updateInvoice: (id, data) => request('PUT', `/api/invoices/${id}`, { body: data }),
    deleteInvoice: (id) => request('DELETE', `/api/invoices/${id}`),
};
